import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Slider } from './ui/slider';
import { toast } from 'sonner';
import { Video, Upload, X, Image, Camera, ChevronLeft, ChevronRight } from 'lucide-react';

import { API_URL } from '../utils/api';

export default function VideoUpload({ 
  onVideoUploaded, 
  onThumbnailUploaded,
  existingVideo = null,
  existingThumbnail = null,
  showThumbnailUpload = true 
}) {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(existingVideo);
  const [thumbnailUrl, setThumbnailUrl] = useState(existingThumbnail);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  
  // Frame selector state
  const [showFrameSelector, setShowFrameSelector] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [framePreview, setFramePreview] = useState(null);
  const [localVideoUrl, setLocalVideoUrl] = useState(null);
  const [videoLoadError, setVideoLoadError] = useState(false);
  
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const frameVideoRef = useRef(null);
  const canvasRef = useRef(null);

  // Clean up local video URL when component unmounts
  useEffect(() => {
    return () => {
      if (localVideoUrl) {
        URL.revokeObjectURL(localVideoUrl);
      }
    };
  }, [localVideoUrl]);

  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);
    
    // Reset any previous errors
    e.target.value = '';

    // More permissive video type check for mobile devices
    // iPhone videos can have various MIME types
    const isVideo = file.type.startsWith('video/') || 
                    file.type === '' || // iPhone sometimes doesn't report MIME type
                    file.type === 'application/octet-stream' ||
                    file.name.toLowerCase().endsWith('.mp4') ||
                    file.name.toLowerCase().endsWith('.mov') ||
                    file.name.toLowerCase().endsWith('.m4v') ||
                    file.name.toLowerCase().endsWith('.avi') ||
                    file.name.toLowerCase().endsWith('.webm');
    
    if (!isVideo) {
      toast.error(`Invalid file type: ${file.type || 'unknown'}. Please select a video file.`);
      return;
    }

    // Check file size (150MB limit)
    const maxSize = 150 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Video too large. Maximum size is 150MB.');
      return;
    }

    // Create local URL for frame selection
    const localUrl = URL.createObjectURL(file);
    setLocalVideoUrl(localUrl);

    setUploading(true);
    setUploadProgress(0);
    toast.info(`Uploading ${file.name} (${(file.size / (1024*1024)).toFixed(1)}MB)...`);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const xhr = new XMLHttpRequest();
      
      // Set a longer timeout for large files on mobile (10 minutes)
      xhr.timeout = 600000;
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
          console.log(`Upload progress: ${progress}%`);
          
          // Show processing message when upload hits 100%
          if (progress === 100) {
            toast.info('Upload complete! Processing video (this may take a minute for iPhone videos)...', {
              duration: 10000
            });
          }
        }
      });

      xhr.addEventListener('load', () => {
        console.log('Upload response:', xhr.status, xhr.responseText);
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            setVideoUrl(response.video_url);
            onVideoUploaded?.(response.video_url);
            toast.success(`Video uploaded successfully! (${response.size_mb}MB)`);
            // Automatically show frame selector after upload
            setShowFrameSelector(true);
          } catch (parseError) {
            console.error('Failed to parse response:', parseError);
            toast.error('Upload completed but response was invalid');
          }
        } else {
          console.error('Upload failed:', xhr.status, xhr.responseText);
          try {
            const error = JSON.parse(xhr.responseText);
            toast.error(error.detail || 'Upload failed');
          } catch {
            toast.error(`Upload failed with status ${xhr.status}`);
          }
          if (localUrl) URL.revokeObjectURL(localUrl);
          setLocalVideoUrl(null);
        }
        setUploading(false);
      });

      xhr.addEventListener('error', (e) => {
        console.error('Upload error event:', e);
        console.error('XHR readyState:', xhr.readyState);
        console.error('XHR status:', xhr.status);
        
        // Provide more specific error message
        let errorMsg = 'Upload failed. ';
        if (xhr.readyState === 0) {
          errorMsg += 'Could not connect to server. Check your internet connection.';
        } else if (xhr.readyState === 4 && xhr.status === 0) {
          errorMsg += 'Request was blocked. This may be a network or CORS issue.';
        } else {
          errorMsg += 'Please check your connection and try again.';
        }
        
        toast.error(errorMsg);
        setUploading(false);
        if (localUrl) URL.revokeObjectURL(localUrl);
        setLocalVideoUrl(null);
      });

      xhr.addEventListener('abort', () => {
        console.log('Upload aborted');
        toast.error('Upload was cancelled');
        setUploading(false);
        if (localUrl) URL.revokeObjectURL(localUrl);
        setLocalVideoUrl(null);
      });
      
      xhr.addEventListener('timeout', () => {
        console.log('Upload timed out');
        toast.error('Upload timed out. Please try with a smaller file or better connection.');
        setUploading(false);
        if (localUrl) URL.revokeObjectURL(localUrl);
        setLocalVideoUrl(null);
      });

      xhr.open('POST', `${API_URL}/api/upload/video`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to start upload: ' + error.message);
      setUploading(false);
      if (localUrl) URL.revokeObjectURL(localUrl);
      setLocalVideoUrl(null);
    }
  };

  // Handle video metadata loaded for frame selector
  const handleVideoLoaded = () => {
    if (frameVideoRef.current) {
      setVideoDuration(frameVideoRef.current.duration);
      setVideoLoadError(false);
      // Seek to 0 to show first frame
      frameVideoRef.current.currentTime = 0;
    }
  };

  const handleVideoLoadError = () => {
    setVideoLoadError(true);
    toast.error('Unable to load video for frame selection. You can still upload a custom cover image.');
  };

  // Handle seeking in video for frame selection
  const handleTimeChange = (value) => {
    const time = value[0];
    setCurrentTime(time);
    if (frameVideoRef.current) {
      frameVideoRef.current.currentTime = time;
    }
  };

  // Capture current frame as thumbnail
  const captureFrame = async () => {
    if (!frameVideoRef.current || !canvasRef.current) return;

    const video = frameVideoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob and upload
    canvas.toBlob(async (blob) => {
      if (!blob) {
        toast.error('Failed to capture frame');
        return;
      }

      setUploadingThumbnail(true);

      const formData = new FormData();
      formData.append('file', blob, 'thumbnail.jpg');

      try {
        const response = await fetch(`${API_URL}/api/upload/thumbnail`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          setThumbnailUrl(data.thumbnail_url);
          onThumbnailUploaded?.(data.thumbnail_url);
          setShowFrameSelector(false);
          toast.success('Cover photo selected!');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to save thumbnail');
        }
      } catch (error) {
        console.error('Thumbnail upload error:', error);
        toast.error('Failed to save thumbnail');
      } finally {
        setUploadingThumbnail(false);
      }
    }, 'image/jpeg', 0.9);
  };

  // Update frame preview when video time changes
  const updateFramePreview = () => {
    if (!frameVideoRef.current || !canvasRef.current) return;
    
    const video = frameVideoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 360;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Check if the frame is all black (HEVC codec issue)
    const imageData = ctx.getImageData(0, 0, Math.min(100, canvas.width), Math.min(100, canvas.height));
    const data = imageData.data;
    let isBlack = true;
    
    // Sample pixels to check if frame is black
    for (let i = 0; i < data.length; i += 40) {
      if (data[i] > 10 || data[i + 1] > 10 || data[i + 2] > 10) {
        isBlack = false;
        break;
      }
    }
    
    if (isBlack && !videoLoadError) {
      setVideoLoadError(true);
    } else if (!isBlack) {
      setFramePreview(canvas.toDataURL('image/jpeg', 0.8));
    }
  };

  // Step through frames
  const stepFrame = (direction) => {
    const step = 0.1; // 100ms step
    const newTime = Math.max(0, Math.min(videoDuration, currentTime + (direction * step)));
    setCurrentTime(newTime);
    if (frameVideoRef.current) {
      frameVideoRef.current.currentTime = newTime;
    }
  };

  const handleThumbnailSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file for the thumbnail');
      return;
    }

    setUploadingThumbnail(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/upload/thumbnail`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setThumbnailUrl(data.thumbnail_url);
        onThumbnailUploaded?.(data.thumbnail_url);
        toast.success('Thumbnail uploaded!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Thumbnail upload failed');
      }
    } catch (error) {
      console.error('Thumbnail upload error:', error);
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const removeVideo = () => {
    setVideoUrl(null);
    setThumbnailUrl(null);
    setLocalVideoUrl(null);
    setShowFrameSelector(false);
    setFramePreview(null);
    onVideoUploaded?.(null);
    onThumbnailUploaded?.(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const removeThumbnail = () => {
    setThumbnailUrl(null);
    onThumbnailUploaded?.(null);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Hidden file input for thumbnail - always rendered for mobile compatibility */}
      <input
        id="thumbnail-upload-input"
        ref={thumbnailInputRef}
        type="file"
        accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif"
        onChange={handleThumbnailSelect}
        className="sr-only"
      />
      
      {/* Video Upload */}
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-2">
          <Video className="inline h-4 w-4 mr-1" />
          Video (up to 150MB)
        </label>
        
        {videoUrl ? (
          <div className="relative rounded-lg overflow-hidden bg-stone-100">
            <video 
              src={videoUrl.startsWith('/api') ? `${API_URL}${videoUrl}` : videoUrl}
              className="w-full max-h-64 object-contain"
              controls
            />
            <button
              onClick={removeVideo}
              className="absolute top-2 right-2 p-1 bg-teal-600 text-white rounded-full hover:bg-gradient-to-r from-sky-500 to-teal-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : uploading ? (
          <div className="border-2 border-dashed border-stone-300 rounded-lg p-6">
            <div className="text-center">
              <Video className="mx-auto h-10 w-10 text-stone-400 animate-pulse" />
              <p className="mt-2 text-sm text-stone-600">Uploading video...</p>
              <Progress value={uploadProgress} className="mt-3 h-2" />
              <p className="mt-1 text-xs text-stone-500">{uploadProgress}%</p>
            </div>
          </div>
        ) : (
          <label 
            htmlFor="video-upload-input"
            className="block border-2 border-dashed border-stone-300 rounded-lg p-6 cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors"
          >
            <div className="text-center">
              <Upload className="mx-auto h-10 w-10 text-stone-400" />
              <p className="mt-2 text-sm text-stone-600">Click to upload video</p>
              <p className="text-xs text-stone-500">MP4, MOV, AVI, WebM (max 150MB)</p>
            </div>
          </label>
        )}
        
        <input
          id="video-upload-input"
          ref={videoInputRef}
          type="file"
          accept="video/*,video/mp4,video/quicktime,video/x-m4v,.mp4,.mov,.m4v,.avi,.webm,.mpeg"
          onChange={handleVideoSelect} 
          className="sr-only"
        />
      </div>

      {/* Frame Selector Modal */}
      {showFrameSelector && localVideoUrl && (
        <div className="bg-stone-900 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-medium flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Select Cover Photo
            </h3>
            <button
              onClick={() => setShowFrameSelector(false)}
              className="text-stone-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Hidden video for frame extraction */}
          <video
            ref={frameVideoRef}
            src={localVideoUrl}
            className="hidden"
            onLoadedMetadata={handleVideoLoaded}
            onSeeked={updateFramePreview}
            onError={handleVideoLoadError}
            playsInline
            muted
          />
          
          {/* Frame Preview */}
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            {videoLoadError ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 p-4 text-center">
                <Video className="h-10 w-10 mb-3 text-stone-500" />
                <p className="text-sm font-medium mb-2">Can't preview this video format</p>
                <p className="text-xs text-stone-500 mb-3">
                  Please upload a custom cover image instead
                </p>
                <label
                  htmlFor="thumbnail-upload-input"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors h-9 px-3 cursor-pointer bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Upload Image
                </label>
              </div>
            ) : framePreview ? (
              <img 
                src={framePreview} 
                alt="Frame preview" 
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-500">
                <p className="text-sm">Loading preview...</p>
              </div>
            )}
          </div>
          
          {/* Time Scrubber */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => stepFrame(-1)}
                className="p-1 bg-stone-700 text-white rounded hover:bg-stone-600"
                title="Previous frame"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              <div className="flex-1">
                <Slider
                  value={[currentTime]}
                  min={0}
                  max={videoDuration || 100}
                  step={0.01}
                  onValueChange={handleTimeChange}
                  className="cursor-pointer"
                />
              </div>
              
              <button
                onClick={() => stepFrame(1)}
                className="p-1 bg-stone-700 text-white rounded hover:bg-stone-600"
                title="Next frame"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            
            <div className="flex justify-between text-xs text-stone-400">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(videoDuration)}</span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={captureFrame}
              disabled={uploadingThumbnail}
              className="flex-1 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
            >
              {uploadingThumbnail ? (
                'Saving...'
              ) : (
                <>
                  <Camera className="h-4 w-4 mr-2" />
                  Use This Frame
                </>
              )}
            </Button>
            <label
              htmlFor="thumbnail-upload-input"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors h-10 px-4 py-2 cursor-pointer border border-stone-600 text-stone-300 hover:bg-stone-800"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Image
            </label>
          </div>
          
          <p className="text-xs text-stone-500 text-center">
            Drag the slider to find the perfect frame, or upload your own image
          </p>
        </div>
      )}

      {/* Thumbnail Display (when not in frame selector mode) */}
      {showThumbnailUpload && videoUrl && !showFrameSelector && (
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            <Image className="inline h-4 w-4 mr-1" />
            Cover Photo
          </label>
          
          {thumbnailUrl ? (
            <div className="flex items-start gap-3">
              <div className="relative w-40 h-24 rounded-lg overflow-hidden">
                <img 
                  src={thumbnailUrl.startsWith('/api') ? `${API_URL}${thumbnailUrl}` : thumbnailUrl}
                  alt="Thumbnail"
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={removeThumbnail}
                  className="absolute top-1 right-1 p-0.5 bg-teal-600 text-white rounded-full hover:bg-gradient-to-r from-sky-500 to-teal-500"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowFrameSelector(true)}
              >
                Change Cover
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowFrameSelector(true)}
                className="flex items-center gap-2"
              >
                <Camera className="h-4 w-4" />
                Select from Video
              </Button>
              <label 
                htmlFor="thumbnail-upload-input"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3 cursor-pointer"
              >
                {uploadingThumbnail ? 'Uploading...' : 'Upload Image'}
              </label>
            </div>
          )}
          
          <p className="text-xs text-stone-500 mt-1">
            A cover photo helps viewers know what the video is about
          </p>
        </div>
      )}
    </div>
  );
}
